// src/pages/LeaderboardPage.js
// Redesigned "Sales Champions" leaderboard — podium top-3, challenger ranks
// 4-10, rank-change badges, category-by-units for both individuals and
// stores. Two entry points:
//   LeaderboardPage — normal in-app page
//   LeaderboardTV   — fullscreen, chrome-free, auto-rotating display for a TV

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import ShareButton from '../components/ShareButton';

const C = {
  blue: '#2563EB', gray50: '#F8FAFC', gray100: '#F1F5F9', gray200: '#E2E8F0',
  gray400: '#94A3B8', gray600: '#475569', gray800: '#1E293B', gray900: '#0F172A',
  white: '#FFFFFF', success: '#059669', warning: '#D97706', danger: '#DC2626',
};

const CAT_KEYS = ['PORT','SIMS','POSTPAID','POSTPAID_ACCEL','DIGI_PLUS','DPLUS_ACCEL','ACCESSORIES','PHONES','PHONES_ACCEL','PLANS'];
const CAT_COLORS = {
  PORT: '#2563EB', SIMS: '#06B6D4', POSTPAID: '#E4002B', POSTPAID_ACCEL: '#DC2626',
  DIGI_PLUS: '#7C3AED', DPLUS_ACCEL: '#6D28D9', ACCESSORIES: '#D97706',
  PHONES: '#059669', PHONES_ACCEL: '#047857', PLANS: '#DB2777',
};
const CAT_LABELS = {PORT:'Port',SIMS:'Prepaid',POSTPAID:'Postpaid',POSTPAID_ACCEL:'Postpaid Accel',DIGI_PLUS:'D+',DPLUS_ACCEL:'D+ Accel',ACCESSORIES:'Accessories',PHONES:'Phones',PHONES_ACCEL:'Phones Accel',PLANS:'Plans'};

// Rank-tier colors — gold / silver / bronze / blue-ish for the rest
const RANK_COLORS = ['#FBBF24', '#38BDF8', '#F87171', '#818CF8'];
const rankColor = (idx) => idx < 3 ? RANK_COLORS[idx] : RANK_COLORS[3];

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
}

// The new podium design: top 3 as a stepped podium (#1 centered and
// tallest, #2 left, #3 right), then ranks 4-10 as a list showing each
// person's gap from the #1 spot in units -- shared between the on-screen
// mobile view and the shareable card so both always look identical and
// only need maintaining in one place.
function LeaderboardPodiumBoard({ ranked, boardType, cat, dense }) {
  const top3 = ranked.slice(0, 3);
  const rest = ranked.slice(3, 10);
  const leaderUnits = ranked[0]?.units ?? 0;
  const accent = CAT_COLORS[cat] || '#D97706';
  // Richer, more saturated medal tones than the original pastel set --
  // these need to pop against the dark podium backdrop below, not sit on
  // white the way the first pass did.
  const medalStyle = [
    { ring: '#FCD34D', bg: 'linear-gradient(160deg,#FDE68A,#F59E0B)', text: '#78350F', crown: '👑', height: dense ? 108 : 128, glow: 'rgba(251,191,36,0.45)' },
    { ring: '#E2E8F0', bg: 'linear-gradient(160deg,#F1F5F9,#94A3B8)', text: '#1E293B', crown: '🥈', height: dense ? 84 : 100, glow: 'rgba(148,163,184,0.35)' },
    { ring: '#FDBA74', bg: 'linear-gradient(160deg,#FED7AA,#C2410C)', text: '#431407', crown: '🥉', height: dense ? 84 : 100, glow: 'rgba(194,65,12,0.4)' },
  ];
  const nameOf = (item) => boardType === 'individual' ? item.agentName : item.storeCode;

  const PodiumSpot = ({ item, style }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
      {style.crown === '👑' && <div style={{ fontSize: 22, marginBottom: 2, filter: 'drop-shadow(0 0 6px rgba(251,191,36,0.7))' }}>👑</div>}
      <div style={{
        width: dense ? 52 : 60, height: dense ? 52 : 60, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: style.bg, color: style.text, fontWeight: 900, fontSize: dense ? 18 : 20,
        border: `3px solid ${style.ring}`, marginBottom: 6, boxShadow: `0 0 0 4px ${style.glow}`,
      }}>
        {initials(nameOf(item))}
      </div>
      <div style={{
        fontSize: dense ? 11.5 : 12.5, fontWeight: 800, color: '#FFFFFF', textAlign: 'center',
        width: '100%', lineHeight: 1.2, minHeight: dense ? 28 : 30,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        wordBreak: 'break-word', overflowWrap: 'break-word', padding: '0 2px',
      }}>
        {nameOf(item)}
      </div>
      <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.65)', fontWeight: 600, marginBottom: 6 }}>{item.storeCode}</div>
      <div style={{
        width: '100%', borderRadius: '10px 10px 0 0', background: style.bg, border: `2px solid ${style.ring}`, borderBottom: 'none',
        height: style.height, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
        boxShadow: `0 -4px 20px ${style.glow}`,
      }}>
        <div style={{ fontSize: dense ? 11 : 12, fontWeight: 800, color: style.text }}>{style.crown}</div>
        <div style={{ fontSize: dense ? 19 : 22, fontWeight: 900, color: style.text }}>{item.units}</div>
      </div>
    </div>
  );

  if (ranked.length === 0) {
    return <div style={{ textAlign: 'center', color: '#94A3B8', padding: 30, fontSize: 13 }}>No data for this period yet.</div>;
  }

  return (
    <div>
      {top3.length > 0 && (
        <div style={{
          position: 'relative', overflow: 'hidden',
          background: 'linear-gradient(160deg,#1E3A8A 0%,#1E40AF 45%,#0F172A 100%)',
          borderRadius: 16, padding: dense ? '18px 14px 0' : '22px 16px 0', marginBottom: 16,
        }}>
          {/* A couple of quiet festive sparkle accents -- corporate enough
              not to look like a party invite, present enough to read as a
              celebration rather than a plain data panel. */}
          <div style={{ position: 'absolute', top: 14, left: 18, fontSize: 15, opacity: 0.55 }}>✨</div>
          <div style={{ position: 'absolute', top: 22, right: 22, fontSize: 12, opacity: 0.4 }}>✨</div>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            {top3[1] && <PodiumSpot item={top3[1]} style={medalStyle[1]} />}
            {top3[0] && <PodiumSpot item={top3[0]} style={medalStyle[0]} />}
            {top3[2] && <PodiumSpot item={top3[2]} style={medalStyle[2]} />}
          </div>
        </div>
      )}
      {rest.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: dense ? 6 : 8 }}>
          {rest.map(item => {
            const gap = leaderUnits - item.units;
            return (
              <div key={item.key} style={{
                display: 'flex', alignItems: 'center', gap: 10, background: `${accent}12`,
                border: `1.5px solid ${accent}30`, borderLeft: `4px solid ${accent}`, borderRadius: 10,
                padding: dense ? '8px 12px' : '10px 14px',
              }}>
                <div style={{
                  width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `${accent}22`, color: accent, fontWeight: 800, fontSize: 12.5,
                }}>
                  {item.rank}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: dense ? 13 : 14, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {nameOf(item)}
                  </div>
                  <div style={{ fontSize: 10.5, color: '#64748B', fontWeight: 600 }}>{item.storeCode}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontWeight: 900, fontSize: dense ? 15 : 16, color: '#0F172A' }}>{item.units}</div>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: gap > 0 ? '#DC2626' : '#059669' }}>
                    {gap > 0 ? `−${gap} behind` : 'Leader'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Dedicated card built specifically for sharing -- top 3 of whatever
// category/board type is currently filtered, laid over the same branded
// background image used for the leaderboard's PPT export (if one has been
// set), so a shared image matches that same look rather than being a
// plain list. This is rendered off-screen purely for html-to-image to
// capture -- the visible, browsable leaderboard list is untouched.
function Top3ShareCard({ ranked, boardType, cat, period, backgroundLoaded, backgroundUrl, filterLabel }) {
  return (
    <div style={{
      width: 420, borderRadius: 16, padding: 24, position: 'relative', overflow: 'hidden',
      backgroundImage: backgroundLoaded ? `url(${backgroundUrl})` : 'linear-gradient(135deg,#1E293B,#0F172A)',
      backgroundSize: 'cover', backgroundPosition: 'center',
    }}>
      {/* Dark overlay so white text stays readable over any photo background */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.55)' }} />
      <div style={{ position: 'relative' }}>
        <div style={{ textAlign: 'center', color: '#FFFFFF', marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '1.5px', opacity: 0.85 }}>DIGIWIRELESS · TOP PERFORMERS</div>
          <div style={{ fontSize: 22, fontWeight: 900, marginTop: 4 }}>🏆 {CAT_LABELS[cat]} — {boardType === 'store' ? 'Top Stores' : 'Top Performers'}</div>
          <div style={{ fontSize: 13, opacity: 0.85, marginTop: 2 }}>{period}{filterLabel ? ` · ${filterLabel}` : ''}</div>
        </div>
        {/* Light panel behind the podium/list so it stays readable over any
            photo background, the same purpose the old rows' white fill served */}
        <div style={{ background: 'rgba(255,255,255,0.97)', borderRadius: 14, padding: '16px 14px 12px' }}>
          <LeaderboardPodiumBoard ranked={ranked} boardType={boardType} cat={cat} dense />
        </div>
      </div>
    </div>
  );
}

function useLeaderboardPeriods() {
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState('');
  useEffect(() => {
    axios.get('/api/sales/periods').then(r => { setPeriods(r.data); if (r.data.length) setPeriod(r.data[0].period); }).catch(() => {});
  }, []);
  const idx = periods.findIndex(p => p.period === period);
  const prevPeriod = idx >= 0 && idx + 1 < periods.length ? periods[idx + 1].period : null;
  return { periods, period, setPeriod, prevPeriod };
}

function useLeaderboardData(period, prevPeriod) {
  const [data, setData] = useState(null);
  const [prevData, setPrevData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!period) return;
    setLoading(true);
    try {
      const calls = [axios.get(`/api/performance/leaderboard-data/${period}`)];
      if (prevPeriod) calls.push(axios.get(`/api/performance/leaderboard-data/${prevPeriod}`));
      const results = await Promise.all(calls);
      setData(results[0].data);
      setPrevData(prevPeriod ? results[1].data : null);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [period, prevPeriod]);

  useEffect(() => { load(); }, [load]);
  return { data, prevData, loading };
}

// Ranks a list of {key, units} descending by units, returns key -> rank (1-based)
function rankMap(items) {
  const sorted = [...items].sort((a, b) => b.units - a.units);
  const map = {};
  sorted.forEach((item, i) => { map[item.key] = i + 1; });
  return map;
}

function placeChangeBadge(placeChange) {
  if (placeChange == null) return { label: 'New', bg: '#334155', fg: '#CBD5E1', icon: '✦' };
  if (placeChange > 0) return { label: `+${placeChange}`, bg: '#064E3B', fg: '#34D399', icon: '▲' };
  if (placeChange < 0) return { label: `${placeChange}`, bg: '#450A0A', fg: '#F87171', icon: '▼' };
  return { label: 'Stable', bg: '#334155', fg: '#CBD5E1', icon: '—' };
}

function applyFilter(agents, filter) {
  if (!filter || filter.type === 'all') return agents;
  if (filter.type === 'tl') return agents.filter(a => a.teamLead === filter.value);
  if (filter.type === 'store') return agents.filter(a => a.storeCode === filter.value);
  if (filter.type === 'stores' && filter.stores) return agents.filter(a => filter.stores.includes(a.storeCode));
  return agents;
}

// Builds a ranked, rank-change-annotated list for a given category, either
// per individual agent or aggregated per store. Rank/place-change is always
// computed against the FULL company dataset (not the active filter) so a
// person's rank reflects true standing; the filter only narrows what's shown.
function useRankedBoard({ data, prevData, cat, boardType, filter, roleFilter }) {
  return useMemo(() => {
    if (!data) return [];

    const buildItems = (ds) => {
      if (!ds) return [];
      if (boardType === 'individual') {
        return ds.agents.map(a => {
          const c = a.categories.find(x => x.key === cat);
          return { key: `${a.storeCode}::${a.agentName}`.toUpperCase(), agentName: a.agentName, storeCode: a.storeCode, teamLead: a.teamLead, role: a.role, units: c ? c.mtd : 0 };
        });
      }
      // store board — sum units per store
      const byStore = {};
      for (const a of ds.agents) {
        const c = a.categories.find(x => x.key === cat);
        if (!byStore[a.storeCode]) byStore[a.storeCode] = { key: a.storeCode, storeCode: a.storeCode, teamLead: a.teamLead, staffCount: 0, units: 0 };
        byStore[a.storeCode].units += c ? c.mtd : 0;
        byStore[a.storeCode].staffCount += 1;
      }
      return Object.values(byStore);
    };

    const currentItems = buildItems(data);
    const prevItems = buildItems(prevData);

    // Role filter (Supervisor/CSR/All) is applied FIRST — it defines a
    // separate leaderboard per role, so "CSR only" shows CSR ranks 1-3,
    // not scattered leftover positions from the full company ranking.
    const matchesRole = (item) => {
      if (boardType !== 'individual' || !roleFilter || roleFilter === 'all') return true;
      const role = item.role || 'Staff';
      return roleFilter === 'supervisor' ? role === 'Supervisor' : role !== 'Supervisor';
    };
    const roleFilteredCurrent = currentItems.filter(matchesRole);
    const roleFilteredPrev = prevItems.filter(matchesRole);

    // Ranks computed on the role-filtered but otherwise FULL (company-wide)
    // set, then the TL/Store filter narrows which rows are displayed —
    // so a Team Lead filter still shows each person's true standing.
    const fullRanks = rankMap(roleFilteredCurrent);
    const prevRanksScoped = rankMap(roleFilteredPrev);

    const filteredItems = boardType === 'individual'
      ? applyFilter(roleFilteredCurrent, filter)
      : roleFilteredCurrent.filter(item => {
          if (!filter || filter.type === 'all') return true;
          if (filter.type === 'tl') return item.teamLead === filter.value;
          if (filter.type === 'store') return item.storeCode === filter.value;
          if (filter.type === 'stores' && filter.stores) return filter.stores.includes(item.storeCode);
          return true;
        });

    return filteredItems
      .map(item => {
        const rank = fullRanks[item.key];
        const prevRank = prevRanksScoped[item.key] ?? null;
        const placeChange = prevRank != null ? prevRank - rank : null;
        return { ...item, rank, prevRank, placeChange };
      })
      .sort((a, b) => a.rank - b.rank);
  }, [data, prevData, cat, boardType, filter, roleFilter]);
}

// Same TL / Store filter pattern used elsewhere in the app
function LeaderboardFilterBar({ agents, filter, setFilter }) {
  const [showStoreFilter, setShowStoreFilter] = useState(false);
  const [checkedStores, setCheckedStores] = useState([]);
  const allStores = useMemo(() => [...new Set(agents.map(a => a.storeCode))].sort(), [agents]);
  const allTLs = useMemo(() => [...new Set(agents.map(a => a.teamLead).filter(Boolean))].sort(), [agents]);

  const applyStoreFilter = () => {
    if (checkedStores.length === 0) setFilter({ type: 'all' });
    else setFilter({ type: 'stores', stores: checkedStores });
    setShowStoreFilter(false);
  };
  const clearStoreFilter = () => { setCheckedStores([]); setFilter({ type: 'all' }); setShowStoreFilter(false); };

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.gray400 }}>Filter:</span>
        <button onClick={() => { setFilter({ type: 'all' }); setCheckedStores([]); }}
          style={{ padding: '5px 12px', borderRadius: 6, border: `1px solid ${filter.type === 'all' ? C.blue : C.gray200}`, background: filter.type === 'all' ? '#EFF6FF' : C.white, color: filter.type === 'all' ? C.blue : C.gray600, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>All</button>
        {allTLs.map(tl => (
          <button key={tl} onClick={() => { setFilter({ type: 'tl', value: tl }); setCheckedStores([]); }}
            style={{ padding: '5px 12px', borderRadius: 6, border: `1px solid ${filter.type === 'tl' && filter.value === tl ? C.blue : C.gray200}`, background: filter.type === 'tl' && filter.value === tl ? '#EFF6FF' : C.white, color: filter.type === 'tl' && filter.value === tl ? C.blue : C.gray600, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            👤 {tl}
          </button>
        ))}
        <button onClick={() => setShowStoreFilter(s => !s)}
          style={{ padding: '5px 12px', borderRadius: 6, border: `1px solid ${filter.type === 'store' || filter.type === 'stores' ? C.blue : C.gray200}`, background: filter.type === 'store' || filter.type === 'stores' ? '#EFF6FF' : C.white, color: filter.type === 'store' || filter.type === 'stores' ? C.blue : C.gray600, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          🏪 Stores {checkedStores.length > 0 ? `(${checkedStores.length})` : ''}
        </button>
      </div>
      {showStoreFilter && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 8, padding: '8px 12px', background: C.gray50, borderRadius: 10, border: `1px solid ${C.gray200}` }}>
          {allStores.map(s => (
            <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 12, cursor: 'pointer', padding: '3px 6px', borderRadius: 6, background: checkedStores.includes(s) ? '#EFF6FF' : C.white, border: `1px solid ${checkedStores.includes(s) ? C.blue : C.gray200}` }}>
              <input type="checkbox" checked={checkedStores.includes(s)} onChange={e => setCheckedStores(prev => e.target.checked ? [...prev, s] : prev.filter(x => x !== s))} style={{ margin: 0 }} />
              {s.replace('DIGIWIRE', 'DW')}
            </label>
          ))}
          <button onClick={applyStoreFilter} style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: C.blue, color: C.white, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Apply</button>
          <button onClick={clearStoreFilter} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.gray200}`, background: C.white, color: C.gray400, fontSize: 11, cursor: 'pointer' }}>Clear</button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// "SALES CHAMPIONS" BOARD — podium top-3 + challenger ranks 4-10
// ═══════════════════════════════════════════════════════════════════════════
function ChampionsBoard({ ranked, title, subtitle, unitLabel, dateRange, backgroundImage }) {
  const top3 = ranked.slice(0, 3);
  const challengers = ranked.slice(3, 10);

  const nameOf = (item) => item.agentName || item.storeCode?.replace('DIGIWIRE', 'DW') || '';
  const subOf = (item) => item.agentName ? item.storeCode.replace('DIGIWIRE', 'DW') : (item.teamLead || '');

  // Exact card geometry/colors matching the official Sales Champions PPT
  // template — rank1 sits higher/taller (podium effect), gold-tinted fill.
  const cardStyle = {
    1: { avatarBg: '#FBBF24', cardFill: '#2D2410', border: '#FBBF24', nameClr: '#FBBF24', big: true },
    2: { avatarBg: '#60A5FA', cardFill: '#1A2332', border: '#60A5FA', nameClr: '#FFFFFF', big: false },
    3: { avatarBg: '#F87171', cardFill: '#1A2332', border: '#F87171', nameClr: '#FFFFFF', big: false },
  };
  const order = [top3.find(i => i.rank === 2), top3.find(i => i.rank === 1), top3.find(i => i.rank === 3)];

  return (
    <div style={{
      backgroundImage: `url(${backgroundImage || '/leaderboard-assets/leaderboard_bg.png'})`, backgroundSize: 'cover', backgroundPosition: 'center',
      borderRadius: 20, padding: '24px 28px', color: C.white, overflow: 'hidden', position: 'relative',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src="/leaderboard-assets/dwpulse_logo.png" alt="DIGIWIRELESS" style={{ height: 44, objectFit: 'contain' }} />
          <span style={{ fontSize: 26, fontWeight: 900 }}>{title}</span>
        </div>
        {dateRange && (
          <span style={{ background: '#334155', padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 800, color: '#CBD5E1' }}>
            📅 {dateRange}
          </span>
        )}
      </div>
      {subtitle && <div style={{ fontSize: 12, color: '#94A3B8', marginTop: -10, marginBottom: 16, fontWeight: 600 }}>{subtitle}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(420px, 1fr) minmax(380px, 1.2fr)', gap: 20 }}>
        {/* Elite Performers — top 3 podium */}
        <div style={{ borderRadius: 16, padding: '18px 16px', border: '0.75px solid #334155' }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#FBBF24', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
            🏆 ELITE PERFORMERS (TOP 3)
          </div>
          {top3.length === 0 ? (
            <div style={{ color: '#64748B', fontSize: 13, textAlign: 'center', padding: 20 }}>No data yet.</div>
          ) : (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', justifyContent: 'center' }}>
              {order.map((item) => {
                if (!item) return null;
                const s = cardStyle[item.rank];
                const badge = placeChangeBadge(item.placeChange);
                return (
                  <div key={item.key} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    width: s.big ? 176 : 148, marginTop: s.big ? 0 : 40,
                  }}>
                    <div style={{ position: 'relative', marginBottom: 12 }}>
                      <div style={{
                        width: s.big ? 92 : 74, height: s.big ? 92 : 74, borderRadius: '50%',
                        background: s.avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: s.big ? 26 : 22, fontWeight: 900, color: '#0F172A',
                      }}>
                        {initials(nameOf(item))}
                      </div>
                    </div>
                    <div style={{
                      background: s.cardFill, border: `1px solid ${s.border}`,
                      borderRadius: 14, padding: '14px 10px', textAlign: 'center', width: '100%',
                      minHeight: s.big ? 202 : 166, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative',
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: '#FFFFFF' }}>{subOf(item)}</div>
                      {item.rank === 1 && <div style={{ fontSize: 22, margin: '2px 0' }}>👑</div>}
                      <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25, color: s.nameClr }}>{nameOf(item)}</div>
                      <div>
                        <div style={{ fontSize: 32, fontWeight: 900, color: s.big ? '#FBBF24' : s.border, margin: '2px 0' }}>{Math.round(item.units)}</div>
                        <span style={{ fontSize: 11, fontWeight: 800, color: badge.fg }}>{badge.icon} {badge.label}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Challenger Ranks 4-10 */}
        <div style={{ borderRadius: 16, padding: '18px 16px', border: '0.75px solid #334155' }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#60A5FA', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
            ⚡ CHALLENGER RANKS (4-{Math.min(10, ranked.length)})
          </div>
          {challengers.length === 0 ? (
            <div style={{ color: '#64748B', fontSize: 13, textAlign: 'center', padding: 20 }}>Not enough entries yet.</div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {challengers.map(item => {
                const badge = placeChangeBadge(item.placeChange);
                return (
                  <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', background: '#141C2B', borderRadius: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#1E293B', color: '#94A3B8', fontSize: 14, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{item.rank < 10 ? `0${item.rank}` : item.rank}</div>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#2A3A52', color: '#60A5FA', fontSize: 14, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{initials(nameOf(item))}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#93C5FD', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nameOf(item)}</div>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 800, minWidth: 44, textAlign: 'right', color: '#FFFFFF' }}>{Math.round(item.units)}</div>
                    <span style={{ fontSize: 13, fontWeight: 800, minWidth: 70, textAlign: 'center', color: badge.fg }}>{badge.icon} {badge.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function asAtDateLabel(d = new Date()) {
  const months = ['JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE','JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER'];
  return `AS AT ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// SPECIAL LEADERBOARD TEMPLATE — Admin's own reference graphic (the
// Christmas "DIGIWIRELESS SALES CHAMPIONS" artwork, saved as
// /leaderboard-assets/special_leaderboard_template.jpg) used AS-IS, with
// live names/stores/units/rank-changes positioned into the exact spots
// that image already has carved out for them (the 3 podium plaques + the
// 7 challenger cards + the "AS AT <date>" ribbon). Nothing about the
// artwork itself is regenerated or restyled -- only these data slots are
// overlaid on top of it. Coordinates below were measured directly off the
// reference image's real pixel grid (1536×1024) and are expressed as
// percentages so the overlay stays aligned at any render size.
const TPL_W = 1536, TPL_H = 1024;
const tplBox = (x1, y1, x2, y2) => ({
  position: 'absolute',
  left: `${(x1 / TPL_W * 100).toFixed(2)}%`,
  top: `${(y1 / TPL_H * 100).toFixed(2)}%`,
  width: `${((x2 - x1) / TPL_W * 100).toFixed(2)}%`,
  height: `${((y2 - y1) / TPL_H * 100).toFixed(2)}%`,
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  textAlign: 'center',
});
const clamp2 = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' };

function deltaBadge(placeChange) {
  if (placeChange == null) return { label: 'NEW', icon: '✦', negative: false };
  if (placeChange > 0) return { label: `+${placeChange}`, icon: '▲', negative: false };
  if (placeChange < 0) return { label: `${placeChange}`, icon: '▼', negative: true };
  return { label: '—', icon: '—', negative: false };
}

// Podium slot geometry (left pedestal = rank 2, center = rank 1, right = rank 3).
// Each slot also masks the sample data baked into the artwork (a plaque /
// pill colored to match that slot) so a different real name never shows
// through underneath the new one. Boxes were measured directly off the
// artwork's own baked-in plaque/pill pixels (color-thresholded + connected-
// component detection, not eyeballed) so the mask fully covers the original
// text with no sliver showing through at any edge.
const PILL_RED = 'linear-gradient(180deg,#A3201E,#6B0D0C)';
const PILL_GREEN = 'linear-gradient(180deg,#1F7A42,#13522C)';
const PODIUM_SLOTS = {
  2: { name: [332, 347, 549, 449], sales: [343, 437, 540, 492], delta: [365, 493, 513, 538], store: [375, 553, 500, 593],
       nameSize: '1.5cqw', salesSize: '1.85cqw', plaque: ['#F5F5F5', '#DEE2E7'], tag: ['#E7E9EC', '#C7CDD4'] },
  1: { name: [610, 300, 975, 420], sales: [650, 422, 892, 492], delta: [680, 491, 860, 539], store: [688, 546, 854, 589],
       nameSize: '1.7cqw', salesSize: '2.0cqw', plaque: ['#ECD28F', '#D4A753'], tag: ['#E6C987', '#C9A44C'] },
  3: { name: [996, 354, 1175, 443], sales: [998, 437, 1191, 492], delta: [1025, 497, 1170, 538], store: [1033, 555, 1157, 593],
       nameSize: '1.5cqw', salesSize: '1.85cqw', plaque: ['#F0CBA4', '#DFA878'], tag: ['#E8C49C', '#C98B54'] },
};
// Challenger cards 4-10: each card's name/sales/delta/initials box, measured
// directly off the artwork per-card (the cards are not perfectly evenly
// spaced, so a single shared pitch formula drifted off by ~60-130px on a
// couple of cards and left old baked text peeking out from under the mask).
const CHALLENGER_SLOTS = [
  { name: [56, 732, 219, 809], sales: [67, 797, 207, 840], delta: [74, 838, 200, 881] },   // rank 4
  { name: [257, 732, 429, 809], sales: [270, 797, 416, 840], delta: [278, 838, 407, 881] }, // rank 5
  { name: [469, 732, 638, 809], sales: [481, 797, 627, 840], delta: [489, 838, 618, 881] }, // rank 6
  { name: [678, 732, 850, 809], sales: [690, 797, 839, 840], delta: [701, 838, 827, 881] }, // rank 7
  { name: [889, 732, 1058, 809], sales: [902, 797, 1046, 840], delta: [910, 838, 1038, 881] }, // rank 8
  { name: [1098, 732, 1268, 809], sales: [1109, 797, 1255, 840], delta: [1120, 838, 1245, 881] }, // rank 9
  { name: [1307, 732, 1471, 809], sales: [1319, 797, 1461, 840], delta: [1326, 838, 1451, 881] }, // rank 10
];
// Initials tag sits centered under each card's delta pill; measured width
// varies card to card (it's just the text, no background plate in the
// artwork), so this uses a consistent generous width rather than per-card
// exact figures.
function challengerTagBox(i) {
  const s = CHALLENGER_SLOTS[i];
  const cx = (s.delta[0] + s.delta[2]) / 2;
  return tplBox(cx - 70, 889, cx + 70, 916);
}

function SpecialLeaderboardTemplate({ ranked, dateLabel }) {
  const top3 = { 1: ranked.find(r => r.rank === 1), 2: ranked.find(r => r.rank === 2), 3: ranked.find(r => r.rank === 3) };
  const challengers = ranked.slice(3, 10);
  const nameOf = (item) => item?.agentName || item?.storeCode?.replace('DIGIWIRE', 'DW') || '';
  const subOf = (item) => item?.agentName ? item.storeCode.replace('DIGIWIRE', 'DW') : (item?.teamLead || '');
  const saleWord = (u) => `${Math.round(u)} ${Math.round(u) === 1 ? 'SALE' : 'SALES'}`;

  return (
    <div style={{
      position: 'relative', width: '100%', aspectRatio: `${TPL_W} / ${TPL_H}`,
      backgroundImage: 'url(/leaderboard-assets/special_leaderboard_template.jpg)',
      backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: 10, overflow: 'hidden',
      containerType: 'inline-size',
    }}>
      {/* "AS AT <date>" — masks over the baked-in sample date with the live one */}
      <div style={{ ...tplBox(503, 138, 1047, 212), background: 'linear-gradient(180deg,#DC2626,#991B1B)', borderRadius: 6 }}>
        <span style={{ fontSize: '1.4cqw', fontWeight: 900, color: '#FFFFFF', letterSpacing: '0.03em', fontFamily: 'Georgia, serif' }}>
          LEADERBOARD {dateLabel}
        </span>
      </div>

      {/* Podium — ranks 1/2/3 */}
      {[1, 2, 3].map(r => {
        const item = top3[r];
        const slot = PODIUM_SLOTS[r];
        if (!item) return null;
        const badge = deltaBadge(item.placeChange);
        return (
          <React.Fragment key={r}>
            <div style={{ ...tplBox(...slot.name), background: `linear-gradient(180deg,${slot.plaque[0]},${slot.plaque[1]})`, borderRadius: 14 }}>
              <div style={{ ...clamp2, fontSize: slot.nameSize, fontWeight: 900, color: '#1E293B', fontFamily: 'Georgia, serif', lineHeight: 1.1 }}>
                {nameOf(item)}
              </div>
            </div>
            <div style={{ ...tplBox(...slot.sales), background: PILL_RED, borderRadius: 999 }}>
              <span style={{ fontSize: slot.salesSize, fontWeight: 900, color: '#FFFFFF', fontFamily: 'Georgia, serif' }}>{saleWord(item.units)}</span>
            </div>
            <div style={{ ...tplBox(...slot.delta), background: badge.negative ? PILL_RED : PILL_GREEN, borderRadius: 999 }}>
              <span style={{ fontSize: '1.05cqw', fontWeight: 800, color: '#FFFFFF' }}>{badge.icon} {badge.label}</span>
            </div>
            <div style={{ ...tplBox(...slot.store), background: `linear-gradient(180deg,${slot.tag[0]},${slot.tag[1]})`, borderRadius: 8 }}>
              <span style={{ fontSize: '1.0cqw', fontWeight: 800, color: '#1E293B' }}>{subOf(item)}</span>
            </div>
          </React.Fragment>
        );
      })}

      {/* Challenger ranks 4-10 */}
      {challengers.map((item, i) => {
        const badge = deltaBadge(item.placeChange);
        const slot = CHALLENGER_SLOTS[i];
        return (
          <React.Fragment key={item.key}>
            <div style={{ ...tplBox(...slot.name), background: '#FAF8F2', borderRadius: 6 }}>
              <div style={{ ...clamp2, fontSize: '0.95cqw', fontWeight: 800, color: '#1E293B', lineHeight: 1.15 }}>{nameOf(item)}</div>
            </div>
            <div style={{ ...tplBox(...slot.sales), background: PILL_RED, borderRadius: 999 }}>
              <span style={{ fontSize: '1.0cqw', fontWeight: 900, color: '#FFFFFF' }}>{saleWord(item.units)}</span>
            </div>
            <div style={{ ...tplBox(...slot.delta), background: badge.negative ? PILL_RED : PILL_GREEN, borderRadius: 999 }}>
              <span style={{ fontSize: '0.9cqw', fontWeight: 800, color: '#FFFFFF' }}>{badge.icon} {badge.label}</span>
            </div>
            <div style={{ ...challengerTagBox(i), background: '#FAF8F2', borderRadius: 6 }}>
              <span style={{ fontSize: '0.85cqw', fontWeight: 700, color: '#475569' }}>{initials(nameOf(item))}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SPECIAL LEADERBOARD — Admin-only. Generates Avinash's own Christmas
// "Sales Champions" artwork (above) with real, live-ranked names/stores/
// units/rank-changes dropped into its podium and challenger slots. Data
// source is the same auto-ranked leaderboard-data feed and category list
// as the regular Leaderboard; the only things that differ are who can open
// this (Admin/Super Admin only -- gated both by the entry point and by the
// underlying call already requiring leaderboard.access) and that it always
// renders onto the fixed reference artwork rather than the app's plain
// default background.
function SpecialLeaderboardModal({ data, prevData, period, onClose }) {
  const [cat, setCat] = useState(CAT_KEYS[0]);
  const [boardType, setBoardType] = useState('individual');
  const captureRef = useRef(null);
  const ranked = useRankedBoard({ data, prevData, cat, boardType, filter: { type: 'all' } });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, overflowY: 'auto' }}>
      <div style={{ background: C.white, borderRadius: 16, padding: 24, maxWidth: 820, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 900, color: C.gray900 }}>🎄 Special Leaderboard</div>
            <div style={{ fontSize: 12.5, color: C.gray600, marginTop: 2 }}>Admin-only · drops live rankings into the Sales Champions artwork, ready to download or share</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, color: C.gray400, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          {[{ k: 'individual', l: '📊 By Individual' }, { k: 'store', l: '🏪 By Store' }].map(v => (
            <button key={v.k} onClick={() => setBoardType(v.k)}
              style={{ padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: boardType === v.k ? 800 : 600,
                background: boardType === v.k ? C.blue : C.white, color: boardType === v.k ? C.white : C.gray600, border: `1px solid ${boardType === v.k ? C.blue : C.gray200}` }}>
              {v.l}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
          {CAT_KEYS.map(k => (
            <button key={k} onClick={() => setCat(k)}
              style={{ padding: '6px 12px', borderRadius: 16, border: `1.5px solid ${cat === k ? '#D97706' : C.gray200}`, background: cat === k ? '#FEF3C7' : C.white, color: cat === k ? '#92400E' : C.gray600, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
              {CAT_LABELS[k]}
            </button>
          ))}
        </div>

        <div ref={captureRef}>
          <SpecialLeaderboardTemplate ranked={ranked} dateLabel={asAtDateLabel()} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <ShareButton targetRef={captureRef} filename={`Special_Leaderboard_${boardType}_${cat}_${period}`} shareTitle="DIGIWIRELESS Sales Champions" />
        </div>
      </div>
    </div>
  );
}

// ── PPT export picker modal ──
function LeaderboardBrandingSettings() {
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = React.useRef(null);

  const loadStatus = () => {
    axios.get('/api/settings/leaderboard-background/status').then(r => setStatus(r.data)).catch(() => {});
  };
  useEffect(() => { loadStatus(); }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    upload(file);
  };

  const upload = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      await axios.post('/api/settings/leaderboard-background', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      loadStatus();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to upload background');
    } finally {
      setUploading(false);
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm('Remove the custom slide background and go back to the default template background?')) return;
    try {
      await axios.delete('/api/settings/leaderboard-background');
      setPreview(null);
      loadStatus();
    } catch (err) { alert('Failed to reset background'); }
  };

  return (
    <div style={{ marginTop: 10, padding: 12, borderRadius: 10, background: C.gray50, border: `1px solid ${C.gray200}` }}>
      <p style={{ fontSize: 12, color: C.gray600, marginBottom: 10 }}>
        Applied to every slide in the exported PowerPoint. Leave unset to use the default template background.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div style={{
          width: 100, height: 56, borderRadius: 8, overflow: 'hidden',
          background: preview ? `url(${preview})` : (status?.hasCustomBackground ? 'url(/api/settings/leaderboard-background)' : C.gray200),
          backgroundSize: 'cover', backgroundPosition: 'center',
          border: `1.5px solid ${C.gray200}`, flexShrink: 0,
        }} />
        <div style={{ fontSize: 12, fontWeight: 700, color: C.gray800 }}>
          {status?.hasCustomBackground ? 'Custom background active' : 'Using default template background'}
        </div>
      </div>
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} style={{ display: 'none' }} />
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
          style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: C.blue, color: C.white, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
          {uploading ? 'Uploading...' : '📤 Upload Image'}
        </button>
        {status?.hasCustomBackground && (
          <button onClick={resetToDefault} style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${C.gray200}`, background: C.white, color: C.gray600, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
            Reset to Default
          </button>
        )}
      </div>
    </div>
  );
}

function PptExportModal({ boardType, cat, onClose, filter }) {
  const [selected, setSelected] = useState([cat]);
  const [busy, setBusy] = useState(false);

  const toggle = (k) => setSelected(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);

  const generate = async () => {
    if (selected.length === 0) return;
    setBusy(true);
    try {
      const params = new URLSearchParams({ categories: selected.join(','), boardType });
      if (filter?.type === 'store') params.append('stores', filter.value);
      else if (filter?.type === 'stores' && filter.stores) params.append('stores', filter.stores.join(','));
      else if (filter?.type === 'tl') params.append('tl', filter.value);
      const res = await axios.get(`/api/performance/leaderboard-ppt?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.setAttribute('download', 'Sales_Champions_Leaderboard.pptx');
      document.body.appendChild(a); a.click(); a.remove();
      onClose();
    } catch (e) { console.error(e); alert('Failed to generate PowerPoint.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: C.white, borderRadius: 16, padding: 24, maxWidth: 460, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.gray900, marginBottom: 6 }}>📥 Export to PowerPoint</div>
        <p style={{ fontSize: 13, color: C.gray600, marginBottom: 14 }}>Pick which category boards to include — one slide per category.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 18, maxHeight: 220, overflowY: 'auto' }}>
          {CAT_KEYS.map(k => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '6px 10px', borderRadius: 8, cursor: 'pointer', background: selected.includes(k) ? '#EFF6FF' : C.gray50, border: `1px solid ${selected.includes(k) ? C.blue : C.gray200}` }}>
              <input type="checkbox" checked={selected.includes(k)} onChange={() => toggle(k)} />
              {CAT_LABELS[k]}
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.gray200}`, background: C.white, color: C.gray600, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={generate} disabled={busy || selected.length === 0}
            style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: selected.length ? C.blue : C.gray200, color: C.white, fontWeight: 700, fontSize: 13, cursor: selected.length ? 'pointer' : 'not-allowed' }}>
            {busy ? 'Generating...' : `Export ${selected.length} slide${selected.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// NORMAL PAGE
// ═══════════════════════════════════════════════════════════════════════════
export default function LeaderboardPage() {
  const navigate = useNavigate();
  const { periods, period, setPeriod, prevPeriod } = useLeaderboardPeriods();
  const [boardType, setBoardType] = useState('individual'); // individual | store
  const [cat, setCat] = useState(CAT_KEYS[0]);
  const [filter, setFilter] = useState({ type: 'all' });
  const [roleFilter, setRoleFilter] = useState('all'); // all | supervisor | csr
  const [showPpt, setShowPpt] = useState(false);
  const [showBg, setShowBg] = useState(false);
  const [showSpecial, setShowSpecial] = useState(false);
  const { can, isSeniorManager } = useAuth();
  const canChangeBackground = can('leaderboard.change_background') === 'true';
  const canDownloadPpt = can('leaderboard.download') === 'true';

  const { data, prevData, loading } = useLeaderboardData(period, prevPeriod);
  const allTLs = useMemo(() => [...new Set((data?.agents || []).map(a => a.teamLead).filter(Boolean))].sort(), [data]);
  const ranked = useRankedBoard({ data, prevData, cat, boardType, filter, roleFilter });
  const isMobile = useIsMobile();
  const leaderboardCaptureRef = useRef(null);

  // Preload the branded background (if one has been set) so the share
  // card knows whether to use it -- a plain <img> gives a reliable
  // load/error signal, which a CSS background-image alone doesn't.
  const [bgLoaded, setBgLoaded] = useState(false);
  const backgroundUrl = '/api/settings/leaderboard-background';
  useEffect(() => {
    const img = new Image();
    img.onload = () => setBgLoaded(true);
    img.onerror = () => setBgLoaded(false);
    img.src = backgroundUrl + `?t=${Date.now()}`;
  }, []);

  if (isMobile) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: C.gray900, margin: 0 }}>🏆 Sales Champions</h1>
            <p style={{ fontSize: 12.5, color: C.gray600, margin: '2px 0 0' }}>Live leaderboard</p>
          </div>
          <ShareButton targetRef={leaderboardCaptureRef} filename={`Leaderboard_${boardType}_${cat}_${filter.type === 'tl' ? filter.value.replace(/\s+/g, '') + '_' : ''}${period}`} shareTitle="Sales Leaderboard" style={{ marginTop: 2 }} />
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12, marginBottom: 10 }}>
          <select value={period} onChange={e => setPeriod(e.target.value)}
            style={{ flex: 1, padding: '9px 10px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, fontSize: 16, background: C.white }}>
            {periods.map(p => <option key={p.period} value={p.period}>{p.period}</option>)}
          </select>
          <select value={boardType} onChange={e => setBoardType(e.target.value)}
            style={{ flex: 1, padding: '9px 10px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, fontSize: 16, background: C.white }}>
            <option value="individual">Individual</option>
            <option value="store">Store</option>
          </select>
        </div>

        {allTLs.length > 0 && (
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 10 }}>
            <button onClick={() => setFilter({ type: 'all' })}
              style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 16, border: `1.5px solid ${filter.type === 'all' ? C.blue : C.gray200}`, background: filter.type === 'all' ? '#EFF6FF' : C.white, color: filter.type === 'all' ? C.blue : C.gray600, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
              All Team Leads
            </button>
            {allTLs.map(tl => (
              <button key={tl} onClick={() => setFilter({ type: 'tl', value: tl })}
                style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 16, border: `1.5px solid ${filter.type === 'tl' && filter.value === tl ? C.blue : C.gray200}`, background: filter.type === 'tl' && filter.value === tl ? '#EFF6FF' : C.white, color: filter.type === 'tl' && filter.value === tl ? C.blue : C.gray600, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                👤 {tl}
              </button>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 12 }}>
          {CAT_KEYS.map(k => (
            <button key={k} onClick={() => setCat(k)}
              style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 16, border: `1.5px solid ${cat === k ? C.gold : C.gray200}`, background: cat === k ? '#FEF3C7' : C.white, color: cat === k ? '#92400E' : C.gray600, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
              {CAT_LABELS[k]}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: C.gray400 }}>Loading…</div>
        ) : (
          <LeaderboardPodiumBoard ranked={ranked} boardType={boardType} cat={cat} />
        )}

        {/* Off-screen -- exists only so the Share button above can capture
            it. Not part of the visible/scrollable page. */}
        <div style={{ position: 'fixed', top: -9999, left: -9999, pointerEvents: 'none' }}>
          <div ref={leaderboardCaptureRef}>
            <Top3ShareCard ranked={ranked} boardType={boardType} cat={cat} period={period} backgroundLoaded={bgLoaded} backgroundUrl={backgroundUrl}
              filterLabel={filter.type === 'tl' ? `👤 ${filter.value}` : filter.type === 'store' ? filter.value.replace('DIGIWIRE', 'DW') : filter.type === 'stores' ? `${filter.stores?.length || 0} stores` : ''} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {showPpt && <PptExportModal boardType={boardType} cat={cat} filter={filter} onClose={() => setShowPpt(false)} />}
      {showSpecial && (
        <SpecialLeaderboardModal data={data} prevData={prevData} period={period} onClose={() => setShowSpecial(false)} />
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 30, fontWeight: 900, color: C.gray900, margin: 0 }}>🏆 Sales Champions</h1>
          <p style={{ fontSize: 13, color: C.gray600, marginTop: 2 }}>Live leaderboard — drives team competition</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={period} onChange={e => setPeriod(e.target.value)}
            style={{ padding: '7px 12px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, fontSize: 13, background: C.white }}>
            {periods.map(p => <option key={p.period} value={p.period}>{p.period}</option>)}
          </select>
          {canChangeBackground && (
            <button onClick={() => setShowBg(s => !s)}
              style={{ padding: '7px 16px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, background: showBg ? C.gray100 : C.white, color: C.gray600, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              🎨 Background
            </button>
          )}
          {canDownloadPpt && (
            <button onClick={() => setShowPpt(true)}
              style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#D97706,#B45309)', color: C.white, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
              📥 Export PPT
            </button>
          )}
          <button onClick={() => navigate('/leaderboard/tv')}
            style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#7C3AED,#5B21B6)', color: C.white, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
            📺 TV Mode
          </button>
          {isSeniorManager && (
            <button onClick={() => setShowSpecial(true)}
              style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#047857,#B91C1C)', color: C.white, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
              🎄 Special Leaderboard
            </button>
          )}
        </div>
      </div>

      {showBg && canChangeBackground && (
        <div style={{ marginBottom: 16, maxWidth: 480 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.gray900, marginBottom: 6 }}>PowerPoint Export — Slide Background Image</div>
          <LeaderboardBrandingSettings />
        </div>
      )}

      <LeaderboardFilterBar agents={data?.agents || []} filter={filter} setFilter={setFilter} />

      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {[{ k: 'individual', l: '📊 By Category' }, { k: 'store', l: '🏪 Store Leaderboard' }].map(v => (
          <button key={v.k} onClick={() => setBoardType(v.k)}
            style={{ padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: boardType === v.k ? 800 : 600,
              background: boardType === v.k ? C.blue : C.white, color: boardType === v.k ? C.white : C.gray600, border: `1px solid ${boardType === v.k ? C.blue : C.gray200}` }}>
            {v.l}
          </button>
        ))}
        <select value={cat} onChange={e => setCat(e.target.value)}
          style={{ padding: '7px 10px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, fontSize: 13, background: C.white, marginLeft: 6 }}>
          {CAT_KEYS.map(k => <option key={k} value={k}>{CAT_LABELS[k]}</option>)}
        </select>
        {boardType === 'individual' && (
          <>
            <span style={{ color: C.gray200 }}>|</span>
            {[{ k: 'all', l: 'All' }, { k: 'supervisor', l: 'Supervisor' }, { k: 'csr', l: 'CSR' }].map(r => (
              <button key={r.k} onClick={() => setRoleFilter(r.k)}
                style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${roleFilter === r.k ? C.blue : C.gray200}`, background: roleFilter === r.k ? '#EFF6FF' : C.white, color: roleFilter === r.k ? C.blue : C.gray600, fontSize: 13, fontWeight: roleFilter === r.k ? 800 : 600, cursor: 'pointer' }}>
                {r.l}
              </button>
            ))}
          </>
        )}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40, color: C.gray400 }}>Loading...</div>}

      {!loading && (
        <ChampionsBoard
          ranked={ranked}
          title={`Sales Champions — Leaderboard (${CAT_LABELS[cat]})`}
          subtitle={boardType === 'individual'
            ? `Ranked by units sold${roleFilter === 'supervisor' ? ' · Supervisors only' : roleFilter === 'csr' ? ' · CSRs only' : ''}`
            : 'Store ranking by total units sold'}
          unitLabel={CAT_LABELS[cat]}
          dateRange={period}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TV MODE — fullscreen, chrome-free, auto-rotating
// ═══════════════════════════════════════════════════════════════════════════
const ROTATE_MS = 10000;

export function LeaderboardTV() {
  const navigate = useNavigate();
  const { periods, period, setPeriod, prevPeriod } = useLeaderboardPeriods();
  const [slideIdx, setSlideIdx] = useState(0); // even = individual, odd = store; cat cycles every 2 slides
  const [catIdx, setCatIdx] = useState(0);

  const { data, prevData } = useLeaderboardData(period, prevPeriod);

  useEffect(() => {
    const id = setInterval(() => {
      setSlideIdx(i => {
        const next = i + 1;
        if (next % 2 === 0) setCatIdx(c => (c + 1) % CAT_KEYS.length);
        return next;
      });
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      axios.get('/api/sales/periods').then(r => { if (r.data.length) setPeriod(r.data[0].period); }).catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [setPeriod]);

  const boardType = slideIdx % 2 === 0 ? 'individual' : 'store';
  const cat = CAT_KEYS[catIdx];
  const ranked = useRankedBoard({ data, prevData, cat, boardType, filter: { type: 'all' } });

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0F172A', padding: 30, overflow: 'hidden' }}>
      <button onClick={() => navigate('/leaderboard')}
        style={{ position: 'absolute', top: 20, right: 20, padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.1)', color: C.white, fontSize: 13, cursor: 'pointer', zIndex: 10 }}>
        ✕ Exit TV Mode
      </button>
      <ChampionsBoard
        ranked={ranked}
        title={`Sales Champions — Leaderboard (${CAT_LABELS[cat]})`}
        subtitle={boardType === 'individual' ? 'Ranked by units sold' : 'Store ranking by total units sold'}
        unitLabel={CAT_LABELS[cat]}
        dateRange={period}
      />
      <div style={{ position: 'absolute', bottom: 18, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 8 }}>
        {CAT_KEYS.map((k, i) => (
          <div key={k} style={{ width: i === catIdx ? 24 : 7, height: 7, borderRadius: 4, background: i === catIdx ? '#60A5FA' : 'rgba(255,255,255,0.2)', transition: 'all 0.3s' }} />
        ))}
      </div>
    </div>
  );
}
